<template>
  <div class="empty-bread" v-if="shouldBeEmpty">
  </div>
  <div class="page-top-component px-3" :class="{ 'is-empty': isEmpty }" v-else>
    <div class="breadcrumbs-container">
      <template v-for="(crumb, index) in crumbs">
        <VMenu
          v-if="crumb.links"
          :key="index"
          bottom
          offsetY
          lazy
          maxHeight="80vh"
          :disabled="!crumb.links"
          class="crumb"
          >
          <div slot="activator" :key="crumb.name" class="crumb-inner">
            <KmIcon v-if="crumb.icon" class="font-light primary--text mr-2" :name="crumb.icon" />
            <StatusIcon v-if="crumb.statusIcon" v-bind="crumb.statusIcon" class="mr-2" />
            <div class="pr-2 crumb-name">
              {{ crumb.name }}
            </div>
            <VIcon v-if="index !== crumbs.length - 1" class="font-light pr-2">
              mdi-chevron-right
            </VIcon>
          </div>
          <VList>
            <VListTile
              v-for="(link, linkIndex) in crumb.links"
              :key="linkIndex"
              :to="link.to"
              :activeClass="link.activeClass"
              >
              <VListTileAction v-if="link.icon" class="action-centered">
                <KmIcon :name="link.icon" />
              </VListTileAction>
              <VListTileTitle>
                {{ link.title }}
              </VListTileTitle>
            </VListTile>
          </VList>
        </VMenu>

        <div v-else :key="index" class="crumb crumb-inner" @click="$router.push(crumb.to)">
          <KmIcon v-if="crumb.icon" class="font-light primary--text mr-2" :name="crumb.icon" />
          <StatusIcon v-if="crumb.statusIcon" v-bind="crumb.statusIcon" class="mr-2" />
          <div class="pr-2 crumb-name">
            {{ crumb.name }}
          </div>
          <VIcon v-if="index !== crumbs.length - 1" class="font-light pr-2">
            mdi-chevron-right
          </VIcon>
        </div>
      </template>
    </div>
    <PortalTarget name="page-options" />
  </div>
</template>

<script>
import KmIcon from '@/components/KmIcon'
import StatusIcon from '@/components/StatusIcon'
import { BUCKET_META_PAGE, BUCKET_FEATURE_PAGES } from '@/router'
import getAppInfos from '@/utils/getAppInfos'
import { getRouteBy } from '@/utils/routeHelpers'

export default {
  name: 'PageBreadcrumbs',

  components: {
    KmIcon,
    StatusIcon,
  },
  computed: {
    shouldBeEmpty() {
      return this.crumbs.length <= 1 && this.$route.name != 'DISTRIBUTED_TRACING_PAGE'
    },
    isEmpty() {
      return this.crumbs.length === 0
    },

    crumbs() {
      const result = []
      const currentRoute = this.$route

      // App
      const currentAppName = this.$store.getters.currentAppName
      if (currentAppName && currentRoute.matched.some(route => route.meta.requireApp)) {
        result.push({
          statusIcon: {
            name: currentAppName,
            query: { appName: currentAppName },
            mode: 'app',
          },
          name: currentAppName,
          links: this.$store.getters.select({ appName: false }).sortedAppNames.map(appName => ({
            title: appName,
            // Force the active class to be undefined (= default value) if the link points to the
            // current app, else an empty string
            activeClass: appName === currentAppName ? undefined : '',
            to: { query: { ...currentRoute.query, app: appName } },
            icon: getAppInfos(appName).icon,
          })),
        })
      }

      for (const matched of currentRoute.matched) {
        // We don't have the 'children' informations in matched routes, and some routes can't have a
        // name, so we have to find the route description using another condition. Since all routes
        // have meta objects, use this for now.
        let desc = getRouteBy(this.$router.options.routes, route => route.meta === matched.meta)

        // Ignore 'index' pages, because they are already in the breadcrumbs
        if (desc.path === '/') continue

        // Ignore bucket meta pages and feature pages
        if (desc.name === BUCKET_META_PAGE || desc.name === BUCKET_FEATURE_PAGES) continue

        // Find the index page
        if (!desc.name && desc.children) {
          desc = desc.children.find(route => route.path === '/')
        }

        result.push({
          icon: desc.meta.icon,
          name: desc.meta.title,
          to: {
            name: desc.name,
            params: currentRoute.params,
            query: { app: currentRoute.query.app },
          },
        })
      }

      if (this.$store.state.pageTitle !== null && result.length) {
        result[result.length - 1].name = this.$store.state.pageTitle
      }

      return result
    },
  },
}
</script>

<style lang="postcss" scoped>
.page-top-component {
  display: flex;
  height: 54px;
  align-items: center;

  &.is-empty {
    height: auto;
  }
}

.empty-bread {
  height: 14px;
}

.breadcrumbs-container {
  display: flex;
  flex-direction: row;
  flex: 1;
  align-items: center;
  min-width: 0;

  /* Only the last crumb can shrink */
  & .crumb:last-child {
    min-width: 0;
    flex-shrink: 1;

    & .crumb-inner {
      min-width: 0;
    }
  }

  & .crumb-inner {
    white-space: nowrap;
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;

    & > svg {
      flex-shrink: 0;
    }

    & > .crumb-name {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

</style>
